import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import { apiLangForRecord } from "@base/modules/appointment/bookingCommon";
import { BookingDraft, BookingStep, withService } from "@base/modules/appointment/regularBookingRules";
import { foldVietnamese } from "@base/modules/doctor/doctorProfile";
import drServiceService from "@base/modules/drService/drServiceService";
import { IDrService } from "@base/modules/drService/interface/drService.interface";

interface IProps {
  draft: BookingDraft;
  onNext: (draft: BookingDraft, step?: BookingStep) => void;
}

/** The clinic's services for this kind of booking - names and descriptions only, prices come from the fee. */
export default function ChooseServiceStep({ draft, onNext }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const lang = apiLangForRecord(draft.record, i18n.resolvedLanguage);
  const [services, setServices] = useState<IDrService[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [keyword, setKeyword] = useState<string>("");
  const [viewing, setViewing] = useState<IDrService>();

  //! function
  useEffect(() => {
    if (!draft.site?.code) {
      return;
    }

    let ignore = false;

    (async () => {
      try {
        setIsLoading(true);
        const resp = await drServiceService.getListForBooking({
          online: !!draft.online,
          siteCode: String(draft.site?.code),
          specialization_code: draft.specializationFilter,
          lang,
          ...(draft.branch?.code ? { site_branch_code: draft.branch.code } : {}),
        });

        if (!ignore) {
          setServices(Array.isArray(resp.data) ? resp.data : []);
        }
      } catch (error) {
        if (!ignore) {
          setServices([]);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [draft.site?.code, draft.branch?.code, draft.online, draft.specializationFilter, lang]);

  const choose = (service: IDrService) => onNext(withService(draft, service));

  //! render
  const needle = foldVietnamese(keyword.trim());
  const visible = needle
    ? services.filter((service) => foldVietnamese(`${service.name ?? ""} ${service.description ?? ""}`).includes(needle))
    : services;

  return (
    <div className="flex flex-col gap-4">
      <CommonStyles.InputUI
        variant="filled"
        allowClear
        value={keyword}
        placeholder={t("booking.searchService")}
        prefix={<CommonIcons.SearchOutlined className="text-common-subtext" />}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setKeyword(event.target.value)}
      />

      {isLoading ? <CommonStyles.LoadingUI /> : null}

      {!isLoading && !visible.length ? (
        <div className="w-full bg-common-background rounded-lg p-4 text-center text-common-subtext text-sm font-normal font-['Inter']">
          {services.length ? t("noResult") : t("booking.noService")}
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {visible.map((service) => (
          <div
            key={service.code ?? service.id}
            onClick={() => choose(service)}
            className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer hover:border-common-primary ${
              service.code === draft.service?.code ? "border-common-primary bg-common-primary50" : "border-common-divider bg-common-white"
            }`}
          >
            <div className="grow min-w-0 flex flex-col gap-1">
              <span className="text-common-maintext text-base font-semibold font-['Inter']">{service.name}</span>
              {service.description ? <span className="text-common-subtext text-sm font-['Inter'] line-clamp-2">{service.description}</span> : null}
            </div>
            <button
              type="button"
              aria-label={t("booking.serviceInfo")}
              className="text-common-primary shrink-0"
              onClick={(event) => {
                event.stopPropagation();
                setViewing(service);
              }}
            >
              <CommonIcons.InfoCircleOutlined />
            </button>
          </div>
        ))}
      </div>

      {viewing ? (
        <CommonStyles.ModalConfirmationUI
          open
          toggle={() => setViewing(undefined)}
          type="normal"
          title={viewing.name}
          okText={t("booking.book")}
          cancelText={t("Close")}
          content={<div className="text-common-maintext text-sm font-['Inter'] whitespace-pre-line">{viewing.description}</div>}
          onConfirm={() => {
            const service = viewing;

            setViewing(undefined);
            choose(service);
          }}
        />
      ) : null}
    </div>
  );
}
